import { useEffect, useMemo, useState } from 'react';
import { joinQuantityUnit, type ShoppingItem } from '@mlc/shared';
import {
  clearCheckedShoppingItems,
  createShoppingItem,
  deleteShoppingItem,
  listShopping,
  updateShoppingItem,
} from './api';
import { Link, paths } from './router';
import { Banner, Button, Checkbox, EmptyState, Icon, Loading, PageHeader, TextInput, buttonClasses } from './ui';

/**
 * Liste de courses (US-28) : le pont entre « j'ai choisi » et « je peux
 * cuisiner ». Sans elle, l'application s'arrêtait au milieu du parcours réel.
 *
 * Trois partis pris d'écran, tous dictés par le lieu d'usage — un magasin, une
 * main occupée par un cabas :
 *
 * - **Cases à 48 px** (`size="cook"`), pas 44 : on coche sans regarder, en
 *   marchant, comme en cuisine.
 * - **Ce qui est coché descend en bas**, estompé. C'est déjà dans le panier ;
 *   ça n'a plus à occuper le haut de l'écran, mais l'effacer d'un coup
 *   empêcherait de vérifier ce qu'on a pris.
 * - **Groupé par rayon**, dans l'ordre où on traverse un magasin. Un rayon se
 *   corrige d'un menu : la correction manuelle fait foi, pas la table de
 *   correspondance.
 */

/** Ordre de traversée d'un magasin, et non ordre alphabétique. Les rayons inconnus suivent. */
const AISLE_ORDER = [
  'Fruits et légumes',
  'Boucherie',
  'Poissonnerie',
  'Crèmerie',
  'Boulangerie',
  'Épicerie',
  'Boissons',
  'Autres',
];

function aisleRank(aisle: string): number {
  const index = AISLE_ORDER.indexOf(aisle);
  return index === -1 ? AISLE_ORDER.length : index;
}

/** Ligne prête à lire : « 200 g farine », « 3 œufs », « Sel, poivre ». */
function itemLabel(item: ShoppingItem): string {
  const quantity = item.quantity === null ? null : String(item.quantity);
  const prefix = joinQuantityUnit(quantity, item.unit, item.quantity);
  return prefix === '' ? item.name : `${prefix} ${item.name}`;
}

/**
 * Export en texte brut (US-28) : c'est le format d'échange réel — on colle sa
 * liste dans un message, pas dans une application tierce.
 */
function toPlainText(items: readonly ShoppingItem[]): string {
  const lines: string[] = [];
  let currentAisle: string | null = null;
  for (const item of items.filter((entry) => entry.checked === 0)) {
    const aisle = item.aisle ?? 'Autres';
    if (aisle !== currentAisle) {
      if (currentAisle !== null) lines.push('');
      lines.push(`${aisle} :`);
      currentAisle = aisle;
    }
    lines.push(`- ${itemLabel(item)}`);
  }
  return lines.join('\n');
}

export function ShoppingList(): React.JSX.Element {
  const [items, setItems] = useState<ShoppingItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    listShopping(controller.signal)
      .then(setItems)
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') setError(e.message);
      });
    return () => { controller.abort(); };
  }, []);

  const sorted = useMemo(() => {
    return [...(items ?? [])].sort((a, b) => {
      if (a.checked !== b.checked) return a.checked - b.checked;
      const rank = aisleRank(a.aisle ?? 'Autres') - aisleRank(b.aisle ?? 'Autres');
      return rank !== 0 ? rank : a.name.localeCompare(b.name, 'fr');
    });
  }, [items]);

  const pending = sorted.filter((item) => item.checked === 0);
  const done = sorted.filter((item) => item.checked === 1);

  /** Regroupement par rayon, dans l'ordre de traversée du magasin. */
  const groups = useMemo(() => {
    const byAisle = new Map<string, ShoppingItem[]>();
    for (const item of pending) {
      const aisle = item.aisle ?? 'Autres';
      byAisle.set(aisle, [...(byAisle.get(aisle) ?? []), item]);
    }
    return [...byAisle.entries()];
  }, [pending]);

  function toggle(item: ShoppingItem): void {
    // Bascule optimiste : cocher doit répondre au doigt, pas au réseau. La
    // liste est de toute façon rechargée à l'ouverture suivante.
    setItems((current) =>
      current?.map((entry) => (entry.id === item.id ? { ...entry, checked: entry.checked === 1 ? 0 : 1 } : entry)) ?? null,
    );
    updateShoppingItem(item.id, { checked: item.checked === 1 ? 0 : 1 }).catch(() => {
      // Hors ligne, la mutation est mise en file (US-12) : l'état affiché est
      // celui qui sera synchronisé, rien à annuler.
    });
  }

  function changeAisle(item: ShoppingItem, aisle: string): void {
    setItems((current) => current?.map((entry) => (entry.id === item.id ? { ...entry, aisle } : entry)) ?? null);
    updateShoppingItem(item.id, { aisle }).catch(() => { /* voir `toggle` */ });
  }

  function remove(item: ShoppingItem): void {
    setItems((current) => current?.filter((entry) => entry.id !== item.id) ?? null);
    deleteShoppingItem(item.id).catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  function addDraft(): void {
    const name = draft.trim();
    if (name === '') return;
    setDraft('');
    createShoppingItem({ name })
      .then(() => listShopping())
      .then(setItems)
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  function clearChecked(): void {
    setItems((current) => current?.filter((entry) => entry.checked === 0) ?? null);
    clearCheckedShoppingItems().catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); });
  }

  function copyToClipboard(): void {
    void navigator.clipboard?.writeText(toPlainText(sorted)).then(
      () => { setCopied(true); },
      () => { setError('Copie impossible : sélectionnez le texte à la main.'); },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Écran de premier niveau : plus de « ← Bibliothèque ». Voir Journal.tsx. */}
      <PageHeader
        title="Liste de courses"
        lede={
          pending.length > 0
            ? `${String(pending.length)} ${pending.length > 1 ? 'articles à prendre' : 'article à prendre'}.`
            : 'Ce que vous ajoutez ici, et ce que les recettes y déposent.'
        }
      >
        {/* Le champ d'ajout est **dans l'en-tête**, groupé dans un creux
            comme la barre d'outils de la bibliothèque : c'est l'outil de cet
            écran, pas un de ses contenus. Il flottait entre le titre et la
            liste, sur une rangée à lui, avec un libellé au-dessus et un
            bouton à côté — trois éléments qu'aucune surface ne reliait. */}
        <div className="flex flex-wrap items-end gap-2 rounded-card bg-surface-sunken p-3">
          <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
            Ajouter un article
            <TextInput
              value={draft}
              placeholder="ex. papier cuisson"
              onChange={(e) => { setDraft(e.target.value); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraft(); } }}
              className="bg-surface"
            />
          </label>
          <Button type="button" variant="secondary" onClick={addDraft} disabled={draft.trim() === ''}>
            Ajouter
          </Button>
        </div>
      </PageHeader>

      {error !== null && <Banner tone="danger">{error}</Banner>}
      {copied && <Banner tone="success">Liste copiée. Collez-la où vous voulez.</Banner>}

      {items === null && <Loading label="votre liste de courses" />}

      {items !== null && items.length === 0 && (
        <EmptyState
          title="Votre liste est vide."
          description="Ouvrez une recette et utilisez « Ajouter à ma liste » : les quantités seront celles des portions que vous avez choisies."
          action={<Link to={paths.library} className={buttonClasses('primary')}>Choisir une recette</Link>}
        />
      )}

      {/*
        Un rayon par section, séparés par un filet plutôt qu'entassés : une
        liste de courses se parcourt rayon par rayon dans le magasin, et rien
        ne marquait où l'un finissait et l'autre commençait.

        **Deux puis trois colonnes sur grand écran.** `columns-*` et non une
        grille : le nombre de rayons varie d'une liste à l'autre, et une
        grille à colonnes fixes laisserait des trous. Le flux typographique
        remplit la première colonne avant d'attaquer la seconde, donc l'ordre
        de lecture — celui des rayons — est préservé.

        `break-inside-avoid` sur chaque section : un rayon coupé en deux au
        pied d'une colonne est précisément ce qu'on ne veut pas devant un
        rayonnage. Deux rayons courts se partagent la colonne, un rayon long
        la prend entière.
      */}
      <div className="gap-x-10 lg:columns-2 xl:columns-3">
      {groups.map(([aisle, aisleItems]) => (
        <section key={aisle} aria-label={aisle} className="flex break-inside-avoid flex-col gap-2 border-t border-border pt-4 pb-4 first:border-t-0 first:pt-0">
          <h2 className="text-label">{aisle}</h2>
          <ul className="flex flex-col gap-1">
            {aisleItems.map((item) => (
              <li key={item.id} className="flex items-center gap-3">
                <Checkbox
                  size="cook"
                  label={`Pris : ${itemLabel(item)}`}
                  checked={false}
                  onChange={() => { toggle(item); }}
                />
                <span className="flex-1 text-base">
                  {item.quantity !== null && (
                    <span className="text-data font-medium">
                      {joinQuantityUnit(String(item.quantity), item.unit, item.quantity)}{' '}
                    </span>
                  )}
                  {item.name}
                </span>
                {/* Le rayon se corrige ici, article par article : la table de
                    correspondance est une aide, pas une autorité. */}
                <label className="sr-only" htmlFor={`aisle-${item.id}`}>
                  Rayon de {item.name}
                </label>
                <select
                  id={`aisle-${item.id}`}
                  value={item.aisle ?? 'Autres'}
                  onChange={(e) => { changeAisle(item, e.target.value); }}
                  className="min-h-touch-min rounded-control border border-border-interactive bg-transparent px-2 text-xs text-foreground-secondary"
                >
                  {AISLE_ORDER.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Retirer ${item.name} de la liste`}
                  onClick={() => { remove(item); }}
                >
                  <Icon name="croix" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ))}
      </div>

      {done.length > 0 && (
        <section aria-label="Déjà pris" className="flex flex-col gap-2 border-t border-border pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-label">Déjà pris ({done.length})</h2>
            <Button type="button" variant="ghost" size="compact" onClick={clearChecked}>
              Vider ce qui est pris
            </Button>
          </div>
          <ul className="flex flex-col gap-1">
            {done.map((item) => (
              <li key={item.id} className="flex items-center gap-3 opacity-60">
                <Checkbox size="cook" label={`Pris : ${itemLabel(item)}`} checked onChange={() => { toggle(item); }} />
                <span className="flex-1 text-base line-through">{itemLabel(item)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {/* Texte brut : le format d'échange réel — on colle sa liste dans un
              message, pas dans une application tierce. */}
          <Button type="button" variant="secondary" onClick={copyToClipboard}>
            Copier la liste
          </Button>
        </div>
      )}
    </div>
  );
}
